import jwtDecode from 'jwt-decode';

const CUSTOM_GROUPS_CLAIM = 'custom:Groups';
const COGNITO_GROUPS_CLAIM = 'cognito:groups';

export interface CognitoTokenGroups {
  /** Native Cognito group membership (e.g. IdP/federation groups from the `cognito:groups` claim). */
  cognitoGroups: string[];
  /** Catch IT application role groups (e.g. "CatchitOwner_users") from the `custom:Groups` claim. */
  customGroups: string[];
}

function decodeIdToken(idToken: string | null | undefined): Record<string, unknown> | null {
  if (!idToken) {
    return null;
  }
  const decoded = jwtDecode<Record<string, unknown>>(idToken);
  console.log('Decoded ID Token:', decoded);
  return decoded;
}

/**
 * Parses the `custom:Groups` claim.
 *
 * @uoa/auth's own parsing (auth.service.mjs) unconditionally does `.slice(1, -1)` on
 * this claim, assuming it is always bracket-wrapped (e.g. "[GroupA,GroupB]"). Our
 * Cognito user pool only wraps it in brackets when the user has multiple groups -
 * with a single group it is a plain string (e.g. "GroupA"), so that slice corrupts
 * the group name by dropping its first and last character (observed:
 * "CatchitOwner_users" became "atchitOwner_user"). This only strips brackets when
 * they are actually present, so it is safe for both formats.
 */
function parseCustomGroupsClaim(raw: unknown): string[] {
  if (typeof raw !== 'string' || raw.length === 0) {
    return [];
  }

  const unwrapped = raw.startsWith('[') && raw.endsWith(']') ? raw.slice(1, -1) : raw;

  return unwrapped
    .split(',')
    .map(group => group.trim())
    .filter(group => group.length > 0);
}

/** Parses the native `cognito:groups` claim, which is already a string array. */
function parseCognitoGroupsClaim(raw: unknown): string[] {
  return Array.isArray(raw) ? raw.filter((group): group is string => typeof group === 'string') : [];
}

export function getCustomGroupsFromIdToken(idToken: string | null | undefined): string[] {
  return parseCustomGroupsClaim(decodeIdToken(idToken)?.[CUSTOM_GROUPS_CLAIM]);
}

export function getCognitoGroupsFromIdToken(idToken: string | null | undefined): string[] {
  return parseCognitoGroupsClaim(decodeIdToken(idToken)?.[COGNITO_GROUPS_CLAIM]);
}

/** Extracts both group claims from a single token decode. */
export function getGroupsFromIdToken(idToken: string | null | undefined): CognitoTokenGroups {
  const decoded = decodeIdToken(idToken);
  return {
    cognitoGroups: parseCognitoGroupsClaim(decoded?.[COGNITO_GROUPS_CLAIM]),
    customGroups: parseCustomGroupsClaim(decoded?.[CUSTOM_GROUPS_CLAIM])
  };
}

export interface CognitoIdentity {
  /** UoA UPI - only present for SAML-federated (UoA SSO) users, from the `identities` claim. */
  upi?: string;
  /** UoA staff numeric employee ID (`custom:EmpID`) - only reliably present for SAML-federated users. */
  empId?: string;
  /** The `cognito:username` claim - always present, for both federated and native (external) users. */
  cognitoUsername: string;
  firstName: string;
  lastName: string;
  email: string;
}

/**
 * Extracts identity fields directly from the ID token.
 *
 * @uoa/auth's own AuthService.getUserInfos() unconditionally reads
 * `decodedToken['identities'][0].userId` to get the UPI. That claim only exists
 * on SAML-federated (UoA SSO) tokens - a native Cognito user (external login)
 * has no `identities` claim at all, so that line throws
 * "Cannot read properties of undefined (reading '0')". This parses the same
 * fields ourselves, tolerating a missing `identities` claim.
 */
export function getIdentityFromIdToken(idToken: string | null | undefined): CognitoIdentity {
  const decoded = decodeIdToken(idToken);
  const identities = decoded?.['identities'];
  const firstIdentity = Array.isArray(identities) ? identities[0] as Record<string, unknown> | undefined : undefined;
  const upi = typeof firstIdentity?.['userId'] === 'string' ? firstIdentity['userId'] as string : undefined;
  const empId = typeof decoded?.['custom:EmpID'] === 'string' ? decoded['custom:EmpID'] as string : undefined;

  return {
    upi,
    empId,
    cognitoUsername: (decoded?.['cognito:username'] as string) || '',
    firstName: (decoded?.['given_name'] as string) || '',
    lastName: (decoded?.['family_name'] as string) || '',
    email: (decoded?.['email'] as string) || ''
  };
}
